import React, { useEffect, useRef, useState } from "react"
import { useApi } from "auth/useApi"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { FieldArray, Form, Formik, FormikProps } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { Box, Checkbox, Divider, Grid, TextField, Typography } from "@mui/material"
import PaperBox from "components/PaperBox"
import theme from "css/theme/light"
import Button from "components/Button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFile, faPlus, faTrashCanXmark } from "@fortawesome/pro-solid-svg-icons"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import TextEditor from "components/Fields/TextEditor"
import ErrorDialog from "components/ErrorDialog"
import MultipleFilePicker from "components/MultipleFilePicker"
import { isJson } from "components/TinyMCE/helpers"
import { TinyMCE } from "components/TinyMCE/TinyMCE"
import { StyledTextField, StyledBox } from "pages/customer/pages/settings/pages/crisisinfo/CrisisInfoForm/CrisisInfoForm.styled"
import { useTranslation } from "react-i18next"

interface ContactInfo {
  name: string
  phoneNumber: string
}

export interface ICrisisInfoAttachment {
  id: number
  name: string
  crisisInfoId: number
  fileId: string
  fileName: string
}

interface CrisisInfoData {
  title: string
  subTitle: string
  otherContactInfos?: ContactInfo[]
  showManagerContactInfo: boolean
  body: string
  attachments?: any[]
  companyId: number
}

interface Props {
  initialCrisisInfo?: CrisisInfoData
  handleSubmit: (objectToSave: any) => void
  result: any
  feedback: any
}

const CrisisInfoForm: React.FC<Props> = (props) => {
  const initialValues: CrisisInfoData = props.initialCrisisInfo
    ? {
      title: props.initialCrisisInfo.title,
      subTitle: props.initialCrisisInfo.subTitle,
      otherContactInfos: props.initialCrisisInfo.otherContactInfos,
      showManagerContactInfo: props.initialCrisisInfo.showManagerContactInfo,
      body: props.initialCrisisInfo.body,
      attachments: props.initialCrisisInfo.attachments,
      companyId: props.initialCrisisInfo.companyId
    }
    : {
      title: "",
      subTitle: "",
      otherContactInfos: [{ name: "", phoneNumber: "" }],
      showManagerContactInfo: false,
      body: "",
      attachments: [],
      companyId: 0
    }

  const [fileUploadResult, uploadFile] = useApi() as [IApiResult, IApiFunc]
  const [fileUpsertResult, upsertFile] = useApi() as [IApiResult, IApiFunc]
  const [fileUpdateResult, updateFile] = useApi() as [IApiResult, IApiFunc]
  const [fileDeleteResult, deleteFile] = useApi() as [IApiResult, IApiFunc]
  const [editFile, setEditFile] = React.useState<null | any>(null)
  const [files, setFiles] = useState([])
  const [filesPreview, setFilesPreview] = useState(initialValues.attachments)
  const [textEditorValue, setTextEditorValue] = useState()
  const formRef = useRef() as any
  const formRefAttachments = useRef<FormikProps<any>>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (files) {
      files.map((file) => {
        return uploadFile.file(apiEndpoints.crisismanagement.uploadattachment, file)
      })
    }
  }, [files, uploadFile])

  useEffect(() => {
    if (fileUploadResult.status === 2) {
      const upsertUrl = apiEndpoints.crisismanagement.uploadattachmenttocrisisinfo.replace("{companyId}", initialValues.companyId.toString())
      upsertFile.post(upsertUrl, fileUploadResult.data)
    }
  }, [fileUploadResult])

  useEffect(() => {
    if (fileUploadResult.data && fileUploadResult.data.name) {
      setFilesPreview((p) => {
        return [...(p as any[]), fileUploadResult.data]
      })
    }
  }, [fileUploadResult])

  useEffect(() => {
    if (fileUpsertResult.status === 2) {
      formRefAttachments?.current?.setFieldValue("attachments", [
        ...formRefAttachments?.current?.values.attachments,
        fileUpsertResult.data
      ])
    }
  }, [fileUpsertResult])

  useEffect(() => {
    if (formRef?.current && textEditorValue) {
      formRef?.current?.setFieldValue("body", textEditorValue)
    }
  }, [textEditorValue])

  useEffect(() => {
    if (fileUpsertResult.status === 2) {
      formRefAttachments?.current?.setFieldValue("attachments", [
        ...formRefAttachments?.current?.values.attachments,
        fileUpsertResult.data
      ])
    }
  }, [fileUpsertResult])

  const saveFile = (valuesObj: any) => {
    if (editFile !== null)
      updateFile.put(apiEndpoints.crisismanagement.updateattachment, valuesObj?.attachments[editFile])
  }

  const onRemoveAttachment = (id: any, index: number) => {
    if (id !== null) {
      deleteFile.del(apiEndpoints.crisismanagement.deleteattachment.replace("{id}", id))
      const newPreviewFiles = [...(filesPreview as any)]
      newPreviewFiles.splice(index, 1)
      setFilesPreview(newPreviewFiles)
    }
  }

  return (
    <>
      <Formik
        innerRef={formRef}
        onSubmit={props.handleSubmit}
        initialValues={initialValues}
      // validationSchema={articleValidationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setFieldValue
        }) => (
          <Form>
            <Grid container direction={"row-reverse"} spacing={3} paddingBottom={10}>
              <Grid item lg={5} md={12} xs={12} justifyContent={"space-between"}>
                <PaperBox sx={{ backgroundColor: theme.palette.primary.light }}>
                  <Grid item xs={12} mb={1}>
                    <Typography variant="h3" color={(theme.palette as any).primary[700]}>
                      {t("global.settings", "Settings")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} display="flex" alignItems="center">
                    <Checkbox
                      onChange={handleChange}
                      id="showManagerContactInfo"
                      checked={values.showManagerContactInfo}
                    />
                    <Typography variant="h6">{t("settings.form.show_contact_info_to_manager", "Show contact information for your nearest manager")}</Typography>
                  </Grid>
                </PaperBox>
              </Grid>

              <Grid item lg={7} md={12}>
                <PaperBox mt={0}>
                  <Grid container>
                    <Typography variant="h3" marginBottom={4}>
                      {t("global.edit_data", "Edit data")}
                    </Typography>

                    <Grid item xs={12}>
                      <Typography variant="h6" marginBottom={1}>
                        {t("global.title", "Title")}
                      </Typography>
                      <StyledTextField
                        autoComplete="off"
                        fullWidth
                        id="title"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.title && errors.title ? true : false}
                        helperText={touched.title && errors.title ? errors.title : null}
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" marginBottom={1} marginTop={2}>
                        {t("global.subtitle", "Subtitle")}
                      </Typography>
                      <StyledTextField
                        autoComplete="off"
                        fullWidth
                        id="subTitle"
                        value={values.subTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.subTitle && errors.subTitle ? true : false}
                        helperText={touched.subTitle && errors.subTitle ? errors.subTitle : null}
                        variant="outlined"
                      />
                    </Grid>

                    <Typography variant="h6" marginBottom={1} marginTop={2} width={"100%"}>
                      {t("settings.companies.contact_info_trafic", "Contact details Traffic area")}
                    </Typography>

                    <FieldArray name="otherContactInfos">
                      {(fieldArrayProps) => {
                        const { push, remove, form } = fieldArrayProps
                        const { values } = form
                        const { otherContactInfos } = values
                        return (
                          <>
                            {otherContactInfos && otherContactInfos.map((contactInfo: ContactInfo, index: number) => (
                              <Grid container xs={12} spacing={2}>
                                <Grid item xs={12} md={5}>
                                  <StyledTextField
                                    autoComplete="off"
                                    fullWidth
                                    id={`otherContactInfos.${index}.name`}
                                    label={t("global.name", "Name")}
                                    value={contactInfo.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.otherContactInfos && errors.otherContactInfos ? true : false
                                    }
                                    helperText={
                                      touched.otherContactInfos && errors.otherContactInfos
                                        ? errors.otherContactInfos
                                        : null
                                    }
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <StyledTextField
                                    autoComplete="off"
                                    fullWidth
                                    id={`otherContactInfos.${index}.phoneNumber`}
                                    label={t("global.phone_number", "Phone number")}
                                    value={contactInfo.phoneNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.otherContactInfos && errors.otherContactInfos ? true : false
                                    }
                                    helperText={
                                      touched.otherContactInfos && errors.otherContactInfos
                                        ? errors.otherContactInfos
                                        : null
                                    }
                                    variant="outlined"
                                    sx={{ marginBottom: 1.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <Button
                                    fullWidth
                                    variant="text"
                                    startIcon={<FontAwesomeIcon icon={faTrashCanXmark as IconProp} />}
                                    sx={{
                                      color: theme.palette.error.main,
                                      minWidth: "90px",
                                      padding: 0,
                                      marginBottom: 1.5,
                                      marginLeft: 1
                                    }}
                                    onClick={() => remove(index)}>
                                    {t("global.remove", "Remove")}
                                  </Button>
                                </Grid>
                              </Grid>
                            ))}

                            <Button
                              variant="outlined"
                              onClick={() => push({ name: "", phoneNumber: "" })}
                              style={{ padding: 5 }}>
                              <Typography sx={{ marginRight: 1 }} variant="bodySmall">{t("global.add_more", "Add more")}</Typography>
                              <FontAwesomeIcon icon={faPlus as IconProp} color={theme.palette.secondary.main} />
                            </Button>
                          </>
                        )
                      }}
                    </FieldArray>

                    <Grid item xs={12}>
                      <Typography variant="h6" marginBottom={1} marginTop={2}>
                        {t("global.other_information", "Other information")}
                      </Typography>
                      {isJson(initialValues.body) ? (
                        <TextEditor
                          value={initialValues.body}
                          setData={(e) => {
                            setTextEditorValue(e)
                          }}
                          uploadApi={apiEndpoints.crisismanagement.uploadbodyattachment}
                        />
                      ) : (
                        <TinyMCE
                          setFormikData={(e) => {
                            setTextEditorValue(e)
                          }}
                          formikValues={{ body: initialValues.body }}
                          uploadMediaUrl={apiEndpoints.crisismanagement.uploadbodyattachment}
                        />
                      )}
                    </Grid>

                    {fileUpsertResult.status === 3 ||
                      (fileUploadResult.status === 3 && (
                        <ErrorDialog title="Uppladdning misslyckades" message="" />
                      ))}
                    <Grid item xs={12}>
                      <Typography variant="h6" marginBottom={1} marginTop={2}>
                        {t("global.upload_document", "Upload documents")}
                      </Typography>
                      <MultipleFilePicker
                        isLoading={fileUpsertResult.status === 1 || fileUploadResult.status === 1}
                        setFiles={setFiles}
                      />
                    </Grid>
                    <Grid container marginTop={1}>
                      {filesPreview?.map((attachment: ICrisisInfoAttachment, index: number) => (
                        <>
                          <Grid item xs={12}>
                            <Box>
                              <Grid container justifyContent={"space-between"} alignItems={"center"}>
                                <Grid item>
                                  <Grid container>
                                    <Grid item>
                                      <FontAwesomeIcon
                                        icon={faFile as IconProp}
                                        color={theme.palette.primary.main}
                                      />
                                    </Grid>
                                    <Grid item>
                                      <Typography width={"100%"} marginLeft={1}>
                                        {attachment.name}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item>
                                  <Button
                                    onClick={() => onRemoveAttachment(attachment.id, index)}
                                    variant="text"
                                    startIcon={<FontAwesomeIcon icon={faTrashCanXmark as IconProp} />}
                                    sx={{
                                      color: theme.palette.error.main
                                    }}>
                                    {t("global.remove", "Remove")}
                                  </Button>
                                </Grid>
                              </Grid>
                              {index != filesPreview.length - 1 ? (
                                <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
                              ) : null}
                            </Box>
                          </Grid>
                        </>
                      ))}
                    </Grid>
                  </Grid>
                </PaperBox>
              </Grid>
            </Grid>
            <Box position={"fixed"} bottom={"32px"} right={"16px"}>
              <Button color="primary" type="submit" result={props.result} feedback={props.feedback}>
                {t("settings.companies.save_crisis_management", "Save crisis management")}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default CrisisInfoForm
